import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { setTableObjectIsLoadingState } from "../../../stateManagement/reducers/TableObjectSlice";
import {
    setToastMessageContainerClearButtonIsClicked,
    showToastMessage,
} from "../../../stateManagement/reducers";

import { Loader } from "../../../widgets/loaders/Loader";
import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import ToastMessageContainer from "../../../widgets/toastMessageContainer/ToastMessageContainer";
import { CardInfoSection, ButtonsOptions } from "../../../widgets/cards/CardInfoSection";

import MapWidget from "../../../widgets/map/mapWidget/MapWidget";
import ObjectCardMap from "./ObjectCardMap";
import { getAllObjectsLayer, getWKTFeatureVectorLayer } from "../../../widgets/map/mapFeatures/layers";

import { useFetchObjectDocumentsQuery } from "../../../api/rtk/base/slices/documentsApiSlice";
import { useObjectCardPrint } from "./hooks/useObjectCardPrint";
import { useObjectInfoCardData } from "./hooks/useObjectInfoCardData";
import { useObjectInfoCardSpecials } from "./hooks/useObjectInfoCardSpecials";

import { buildObjectInfoCardStructure } from "./structure";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { formatObject } from "../../../features/dataFormatFunctions/formatObject";
import { buildObjectCardHeaderTitle } from "../../../features/objectLabels/objectLabelsConfig";

type ObjectType = { name: string; captionSingular?: string | null };

export function ObjectInfoCard() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { objectId, objectTypeName } = useParams<{ objectId: string; objectTypeName: string }>();

    const printRef = useRef<HTMLDivElement>(null);

    const [mapState, setMapState] = useState<any>(null);
    const [printMapState, setPrintMapState] = useState<any>(null);

    const [additionalLayersForPrint, setAdditionalLayersForPrint] = useState<any[]>([]);
    const [objectGeometryIsError, setObjectGeometryIsError] = useState(false);
    const [objectGeometryIsFetching, setObjectGeometryIsFetching] = useState(true);

    const [fitExtent, setFitExtent] = useState<any>(null);
    const [fitExtentPadding, setFitExtentPadding] = useState<number[]>([]);

    const permanentLayersPrintMap = useMemo(() => [getAllObjectsLayer()], []);

    // -----------------------------
    // Data (hooks)
    // -----------------------------
    const {
        queryType,
        cardsStructure,
        isFetchingCardsStructure,
        isCardsStructureError,
        objectInfo,
        isFetchingObjectInfo,
        objectInfoError,
    } = useObjectInfoCardData({ objectTypeName, objectId });

    // -----------------------------
    // WebStorage (read once)
    // -----------------------------
    const objectTypes = useMemo<ObjectType[]>(() => {
        const stored = getItemWebStorage("objectTypes");
        return Array.isArray(stored) ? (stored as ObjectType[]) : [];
    }, []);

    const captionSingular = useMemo(() => {
        if (!objectTypeName) return null;
        return objectTypes.find((t) => t?.name === objectTypeName)?.captionSingular ?? null;
    }, [objectTypes, objectTypeName]);

    // -----------------------------
    // Derived view data
    // -----------------------------
    const formattedData = useMemo(() => (objectInfo ? formatObject(objectInfo) : null), [objectInfo]);

    const title = useMemo(() => {
        return buildObjectCardHeaderTitle({
            objectTypeName,
            objectTypeCaptionSingular: captionSingular,
            formattedObjectInfo: formattedData,
        });
    }, [objectTypeName, captionSingular, formattedData]);

    const pageTitle = title?.trim() ? title : "Карточка объекта";

    // Print (depends on title)
    useObjectCardPrint(printRef as any, `IndorGeo - ${pageTitle}`);

    // -----------------------------
    // Documents
    // -----------------------------
    const hasDocumentsLink = !!objectInfo?.["DocumentLinks@xdata.proxy"];
    const skipDocuments = !hasDocumentsLink || !objectTypeName || !objectId;

    const { data: objectDocuments, isLoading: objectDocumentsAreLoading } = useFetchObjectDocumentsQuery(
        { urlType: queryType, objectType: objectTypeName ?? "", objectId: objectId ?? "" },
        { skip: skipDocuments }
    );

    const baseButtonsOptions: ButtonsOptions = useMemo(() => {
        const numberOfDocuments = objectDocuments?.value?.length ?? 0;

        return {
            documents: {
                numberOfDocuments,
                disabled: !hasDocumentsLink || numberOfDocuments === 0 || objectDocumentsAreLoading,
                isLoading: objectDocumentsAreLoading,
            },
        };
    }, [objectDocuments, objectDocumentsAreLoading, hasDocumentsLink]);

    // -----------------------------
    // Specials
    // -----------------------------
    const { computedFields, extraButtonsBySection, buttonsOptions: specialsButtonsOptions } =
        useObjectInfoCardSpecials({
            objectTypeName,
            objectId,
            objectInfo,
            urlType: queryType,
        });

    const dataWithComputed = useMemo(() => {
        if (!formattedData) return formattedData;
        return { ...formattedData, ...computedFields };
    }, [formattedData, computedFields]);

    const baseSections = useMemo(() => {
        const typeKey = objectTypeName ?? "";
        const raw = (cardsStructure as any)?.[typeKey] ?? [];
        return buildObjectInfoCardStructure(raw);
    }, [cardsStructure, objectTypeName]);

    const sections = useMemo(() => {
        if (!baseSections?.length) return baseSections;

        // 1) Inject extra buttons per section
        let next = baseSections.map((s: any) => {
            const extraBtns = (extraButtonsBySection as any)?.[s.sectionName] ?? [];
            if (!extraBtns.length) return s;

            const columns = s.columns ?? [[]];
            return { ...s, columns: [...columns, extraBtns] };
        });

        // 2) Add standard buttons to the end of the first section
        const standardButtons: any[] = [];

        if (hasDocumentsLink) {
            standardButtons.push({ format: "button", type: "documents", caption: "Документы" });
        }
        standardButtons.push({ format: "button", type: "showOnMap", caption: "Показать на карте" });

        if (standardButtons.length && next[0]) {
            const first = next[0];
            const columns = first.columns ?? [[]];
            next[0] = { ...first, columns: [...columns, standardButtons] };
        }

        // 3) Keep only sections with at least one non-empty column
        next = next.filter((s: any) => (s.columns ?? []).some((fields: any[]) => fields.length > 0));

        return next;
    }, [baseSections, extraButtonsBySection, hasDocumentsLink]);

    // -----------------------------
    // Map event handlers (computed, not stored)
    // -----------------------------
    const mapEventHandlers = useMemo(() => {
        function applyOpenFullSizeMap(map: any) {
            const clickFunc = () => {
                if (!objectTypeName || !objectId) return;
                navigate(`/app/map/${objectTypeName}/${objectId}`);
            };
            map?.on("singleclick", clickFunc);
            return { off: () => map?.un("singleclick", clickFunc) };
        }

        function applySynchronizePrintMap(map: any) {
            const synchFunc = () => {
                if (!mapState || !printMapState) return;
                printMapState.getView().fit(mapState.getView().calculateExtent(), {
                    size: printMapState.getSize(),
                });
            };
            map?.on("moveend", synchFunc);
            return { off: () => map?.un("moveend", synchFunc) };
        }

        return [
            { name: "openFullSizeMap", func: applyOpenFullSizeMap },
            { name: "synchronizePrintMap", func: applySynchronizePrintMap },
        ];
    }, [navigate, objectTypeName, objectId, mapState, printMapState]);

    // -----------------------------
    // Effects: mount/unmount
    // -----------------------------
    useEffect(() => {
        return () => {
            dispatch(
                setTableObjectIsLoadingState({
                    objectInfo: null,
                    tableObjectsStructure: null,
                    loadingCoords: false,
                    loadingStructure: false,
                    loadingData: false,
                } as any)
            );
        };
    }, [dispatch]);

    // If structure loaded but type missing -> error page
    useEffect(() => {
        if (isFetchingCardsStructure) return;
        if (!cardsStructure || !objectTypeName) return;

        if (!(cardsStructure as any)[objectTypeName]) {
            navigate("/app/error");
        }
    }, [cardsStructure, objectTypeName, isFetchingCardsStructure, navigate]);

    // Sync loading state for header widgets
    useEffect(() => {
        dispatch(
            setTableObjectIsLoadingState({
                loadingStructure: isFetchingCardsStructure,
                loadingData: isFetchingObjectInfo,
            } as any)
        );
    }, [dispatch, isFetchingCardsStructure, isFetchingObjectInfo]);

    // Clear toast close button while any loading is active
    const isAnyLoading = isFetchingObjectInfo || isFetchingCardsStructure || objectGeometryIsFetching;
    useEffect(() => {
        if (isAnyLoading) dispatch(setToastMessageContainerClearButtonIsClicked(true));
    }, [dispatch, isAnyLoading]);

    // Toasts / navigation on errors (show once per “truthy” transition)
    const shownRef = useRef({ structure: false, geometry: false, object: false });

    useEffect(() => {
        if (!isCardsStructureError) {
            shownRef.current.structure = false;
            return;
        }
        if (shownRef.current.structure) return;

        dispatch(showToastMessage({ messageText: "Не удалось загрузить страницу", messageType: "error" }));
        shownRef.current.structure = true;
    }, [dispatch, isCardsStructureError]);

    useEffect(() => {
        if (!objectGeometryIsError) {
            shownRef.current.geometry = false;
            return;
        }
        if (shownRef.current.geometry) return;

        dispatch(
            showToastMessage({
                messageText: "Не удалось загрузить положение объекта на карте",
                messageType: "error",
            })
        );
        shownRef.current.geometry = true;
    }, [dispatch, objectGeometryIsError]);

    useEffect(() => {
        if (!objectInfoError) {
            shownRef.current.object = false;
            return;
        }

        const error: any = objectInfoError;

        if (error?.data?.error?.code === "EntityNotFound") {
            navigate("/app/error");
            return;
        }

        if (shownRef.current.object) return;

        dispatch(
            showToastMessage({
                messageText: "Не удалось загрузить данные об объекте",
                messageType: "error",
            })
        );
        shownRef.current.object = true;
    }, [dispatch, objectInfoError, navigate]);

    // -----------------------------
    // Map callbacks
    // -----------------------------
    const handleGeometryLoaded = useCallback((geometry: any, extent: any) => {
        setFitExtent(extent);
        setFitExtentPadding([50, 50, 50, 50]);
        setAdditionalLayersForPrint([getWKTFeatureVectorLayer(geometry)]);
    }, []);

    const handleShowOnMapClick = useCallback(() => {
        if (!objectTypeName || !objectId) return;
        navigate(`/app/map/${objectTypeName}/${objectId}`);
    }, [navigate, objectTypeName, objectId]);

    // -----------------------------
    // Render
    // -----------------------------
    const showLoader = isFetchingObjectInfo || isFetchingCardsStructure;

    return (
        <>
            <ContentHeader title={pageTitle} type="card" option="object" />

            <div className="card-div object-info-card-div" ref={printRef}>
                <ToastMessageContainer />

                {showLoader ? (
                    <Loader />
                ) : (
                    <div>
                        <h5 className="print-title">{pageTitle}</h5>

                        {/* Контейнер для отображения печатного варианта страницы */}
                        <div style={{ display: "none" }}>Дата номер</div>

                        {/* Секции */}
                        {formattedData &&
                            sections?.map((sectionOptions: any) => (
                                <CardInfoSection
                                    key={sectionOptions.sectionName}
                                    sectionOptions={sectionOptions}
                                    data={dataWithComputed}
                                    buttonsOptions={{
                                        ...baseButtonsOptions,
                                        ...specialsButtonsOptions,
                                        showOnMap: {
                                            disabled: objectGeometryIsError,
                                            isLoading: objectGeometryIsFetching,
                                            onClick: handleShowOnMapClick,
                                        },
                                    }}
                                />
                            ))}

                        {/* Карта */}
                        {!objectGeometryIsError && (
                            <ObjectCardMap
                                urlType={queryType}
                                objectTypeName={objectTypeName}
                                objectId={objectId}
                                mapEventHandlers={mapEventHandlers}
                                mapInfoWidgetIsShown={false}
                                onMapReady={(map: any) => setMapState(map)}
                                onGeometryLoaded={handleGeometryLoaded}
                                onLoadingChange={(isLoading: boolean) => setObjectGeometryIsFetching(isLoading)}
                                onError={(isError: boolean) => setObjectGeometryIsError(isError)}
                            />
                        )}

                        {/* Печатная карта */}
                        {!objectGeometryIsError && (
                            <div className="map-to-print-div">
                                <div className="page-break" />
                                <MapWidget
                                    target="table-object-info-map-print"
                                    mapStateReturnFunc={(map: any) => setPrintMapState(map)}
                                    additionalLayers={additionalLayersForPrint}
                                    permanentLayers={permanentLayersPrintMap}
                                    propsFitExtent={fitExtent}
                                    propsFitExtentPadding={fitExtentPadding}
                                    mapSizeClass="table-object-info-map-print"
                                    mapInfoWidgetIsShown={false}
                                    showMapLoader={false}
                                    mapType="card"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
